import { useCallback, useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "react-toastify";
import { apiUrl, apiUrlDev } from "../../globalVariables";
import "./NotionBriefings.css";

const getBriefingBaseUrls = () => {
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  return isLocalhost ? [apiUrl, apiUrlDev] : [apiUrl];
};

const fetchJson = async (url) => {
  const response = await fetch(url);
  const contentType = response.headers.get("content-type") || "";
  const text = await response.text();

  if (!contentType.includes("application/json")) {
    const looksLikeHtml = text.trim().startsWith("<!DOCTYPE") ||
      text.trim().startsWith("<html");
    throw new Error(
      looksLikeHtml
        ? "The Notion preview endpoint is not deployed yet."
        : "The Notion preview endpoint did not return JSON.",
    );
  }

  return {
    response,
    payload: text ? JSON.parse(text) : null,
  };
};

const getText = (richText = []) =>
  richText.map((piece) => piece?.plain_text || "").join("");

const RichText = ({ text = [] }) => {
  if (!Array.isArray(text) || text.length === 0) return null;

  return text.map((piece, index) => {
    const annotations = piece?.annotations || {};
    let content = piece?.plain_text || "";

    if (!content) return null;

    if (annotations.code) content = <code>{content}</code>;
    if (annotations.bold) content = <strong>{content}</strong>;
    if (annotations.italic) content = <em>{content}</em>;
    if (annotations.strikethrough) content = <del>{content}</del>;
    if (annotations.underline) content = <u>{content}</u>;

    if (piece?.href) {
      content = (
        <a href={piece.href} target="_blank" rel="noreferrer">
          {content}
        </a>
      );
    }

    return (
      <span key={`${piece?.plain_text || "text"}-${index}`}>{content}</span>
    );
  });
};

const NotionBlock = ({ block }) => {
  if (!block?.type) return null;

  const value = block[block.type] || {};
  const richText = value.rich_text || value.text || [];
  const children = Array.isArray(block.children) ? block.children : [];
  const renderedChildren = children.length ? (
    <div className="nb-block-children">
      {children.map((child) => (
        <NotionBlock key={child.id} block={child} />
      ))}
    </div>
  ) : null;

  switch (block.type) {
    case "heading_1":
      return (
        <h1 className="nb-doc-h1">
          <RichText text={richText} />
        </h1>
      );
    case "heading_2":
      return (
        <h2 className="nb-doc-h2">
          <RichText text={richText} />
        </h2>
      );
    case "heading_3":
      return (
        <h3 className="nb-doc-h3">
          <RichText text={richText} />
        </h3>
      );
    case "paragraph":
      return getText(richText).trim() ? (
        <p className="nb-doc-p">
          <RichText text={richText} />
        </p>
      ) : (
        <div className="nb-doc-spacer" />
      );
    case "bulleted_list_item":
      return (
        <div className="nb-doc-list-row">
          <span className="nb-doc-bullet">•</span>
          <div>
            <RichText text={richText} />
            {renderedChildren}
          </div>
        </div>
      );
    case "numbered_list_item":
      return (
        <div className="nb-doc-list-row">
          <span className="nb-doc-bullet">1.</span>
          <div>
            <RichText text={richText} />
            {renderedChildren}
          </div>
        </div>
      );
    case "to_do":
      return (
        <label className="nb-doc-todo">
          <input type="checkbox" checked={!!value.checked} readOnly />
          <span>
            <RichText text={richText} />
          </span>
        </label>
      );
    case "quote":
      return (
        <blockquote className="nb-doc-quote">
          <RichText text={richText} />
        </blockquote>
      );
    case "callout":
      return (
        <div className="nb-doc-callout">
          <span className="nb-doc-callout-icon">{value.icon?.emoji || "i"}</span>
          <div>
            <RichText text={richText} />
          </div>
        </div>
      );
    case "code":
      return (
        <pre className="nb-doc-code">
          <code>{getText(richText)}</code>
        </pre>
      );
    case "divider":
      return <hr className="nb-doc-divider" />;
    case "image": {
      const src = value.type === "external" ? value.external?.url : value.file?.url;
      if (!src) return null;
      return (
        <figure className="nb-doc-figure">
          <img src={src} alt={getText(value.caption) || "Notion briefing"} />
          {value.caption?.length ? (
            <figcaption>
              <RichText text={value.caption} />
            </figcaption>
          ) : null}
        </figure>
      );
    }
    case "bookmark":
    case "link_preview":
      return value.url ? (
        <a className="nb-doc-bookmark" href={value.url} target="_blank" rel="noreferrer">
          {value.url}
        </a>
      ) : null;
    case "child_page":
      return <div className="nb-doc-child-page">{value.title}</div>;
    default:
      return getText(richText).trim() ? (
        <p className="nb-doc-p">
          <RichText text={richText} />
        </p>
      ) : null;
  }
};

const BriefingPreview = ({ detail, entry, error, isLoading }) => {
  if (isLoading) {
    return <div className="nb-preview-state text-muted">Loading preview...</div>;
  }

  if (error) {
    return (
      <div className="nb-preview-state">
        <h3 className="nb-open-title">Preview unavailable</h3>
        <p className="nb-open-copy">{error}</p>
        <a href={entry.url} target="_blank" rel="noreferrer" className="btn btn-primary">
          Open in Notion
        </a>
      </div>
    );
  }

  if (detail?.markdown) {
    return (
      <div className="nb-doc">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{detail.markdown}</ReactMarkdown>
      </div>
    );
  }

  if (detail?.content) {
    return (
      <div className="nb-doc">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{detail.content}</ReactMarkdown>
      </div>
    );
  }

  if (Array.isArray(detail?.blocks) && detail.blocks.length > 0) {
    return (
      <div className="nb-doc">
        {detail.blocks.map((block) => (
          <NotionBlock key={block.id} block={block} />
        ))}
      </div>
    );
  }

  return (
    <div className="nb-preview-state">
      <h3 className="nb-open-title">Preview unavailable</h3>
      <p className="nb-open-copy">
        The briefing exists, but no renderable page content was returned.
      </p>
      <a href={entry.url} target="_blank" rel="noreferrer" className="btn btn-primary">
        Open in Notion
      </a>
    </div>
  );
};

/**
 * Briefings page.
 *
 * Surfaces the four "Addy AI" Notion sections (Weekly Exec Meeting Prep,
 * Weekly Executive Briefing, Daily Briefing, Daily Client Status). The user
 * picks a category, then a date, and views the selected briefing inline from
 * backend-provided Notion content.
 */
const NotionBriefingsPage = ({ onReady }) => {
  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeSectionKey, setActiveSectionKey] = useState("");
  const [selectedEntryId, setSelectedEntryId] = useState("");
  const [search, setSearch] = useState("");
  const [briefingDetails, setBriefingDetails] = useState({});
  const [detailLoadingId, setDetailLoadingId] = useState("");
  const [detailError, setDetailError] = useState("");

  const fetchSections = useCallback(async () => {
    const userId = getAuth()?.currentUser?.uid;
    if (!userId) {
      setError("Admin user not found. Please refresh and sign in again.");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError("");

    let lastError = "Failed to load briefings.";
    let loaded = false;

    for (const baseUrl of getBriefingBaseUrls()) {
      try {
        const url = new URL(baseUrl + "/notion-briefings/sections");
        url.searchParams.append("userId", userId);
        const { response, payload } = await fetchJson(url.toString());
        if (!response.ok || !payload?.success) {
          lastError = payload?.reason || `Briefings API failed on ${baseUrl}`;
          continue;
        }
        const data = Array.isArray(payload.data) ? payload.data : [];
        setSections(data);
        loaded = true;
        break;
      } catch (innerError) {
        lastError = innerError?.message || `Could not reach ${baseUrl}`;
      }
    }

    if (!loaded) {
      setError(lastError);
      setSections([]);
      toast.error(lastError);
    }
    setIsLoading(false);
  }, []);

  const fetchBriefingDetail = useCallback(async (entry) => {
    const userId = getAuth()?.currentUser?.uid;
    if (!userId || !entry?.id) return;

    setDetailError("");

    if (
      briefingDetails[entry.id] ||
      entry.blocks ||
      entry.markdown ||
      entry.content
    ) {
      return;
    }

    setDetailLoadingId(entry.id);

    let lastError = "Could not load this briefing preview.";
    let loaded = false;

    for (const baseUrl of getBriefingBaseUrls()) {
      try {
        const url = new URL(baseUrl + "/notion-briefings/page");
        url.searchParams.append("userId", userId);
        url.searchParams.append("entryId", entry.id);
        if (entry.pageId) url.searchParams.append("pageId", entry.pageId);

        const { response, payload } = await fetchJson(url.toString());

        if (!response.ok || !payload?.success) {
          lastError = payload?.reason || `Briefing preview failed on ${baseUrl}`;
          continue;
        }

        setBriefingDetails((current) => ({
          ...current,
          [entry.id]: payload.data || {},
        }));
        loaded = true;
        break;
      } catch (innerError) {
        lastError = innerError?.message || `Could not reach ${baseUrl}`;
      }
    }

    if (!loaded) {
      setDetailError(lastError);
    }

    setDetailLoadingId("");
  }, [briefingDetails]);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  useEffect(() => {
    if (typeof onReady === "function") onReady();
  }, [onReady]);

  // Default to the first section once data arrives.
  useEffect(() => {
    if (!activeSectionKey && sections.length > 0) {
      setActiveSectionKey(sections[0].key);
    }
  }, [sections, activeSectionKey]);

  const activeSection = useMemo(
    () => sections.find((section) => section.key === activeSectionKey) || null,
    [sections, activeSectionKey],
  );

  const visibleEntries = useMemo(() => {
    const entries = activeSection?.entries || [];
    const query = search.trim().toLowerCase();
    if (!query) return entries;
    return entries.filter((entry) =>
      `${entry.dateLabel} ${entry.title}`.toLowerCase().includes(query),
    );
  }, [activeSection, search]);

  // Auto-select the newest entry whenever the visible list changes.
  useEffect(() => {
    if (visibleEntries.length === 0) {
      setSelectedEntryId("");
      return;
    }
    const stillVisible = visibleEntries.some(
      (entry) => entry.id === selectedEntryId,
    );
    if (!stillVisible) {
      setSelectedEntryId(visibleEntries[0].id);
    }
  }, [visibleEntries, selectedEntryId]);

  const selectedEntry = useMemo(
    () => visibleEntries.find((entry) => entry.id === selectedEntryId) || null,
    [visibleEntries, selectedEntryId],
  );

  useEffect(() => {
    if (selectedEntry) {
      fetchBriefingDetail(selectedEntry);
    } else {
      setDetailError("");
    }
  }, [selectedEntry, fetchBriefingDetail]);

  const selectedDetail = useMemo(() => {
    if (!selectedEntry) return null;
    return {
      ...selectedEntry,
      ...(briefingDetails[selectedEntry.id] || {}),
    };
  }, [briefingDetails, selectedEntry]);

  const handleSelectSection = (key) => {
    setActiveSectionKey(key);
    setSearch("");
    setSelectedEntryId("");
  };

  return (
    <div
      className="container-fluid NotionBriefingsPageContainer d-flex flex-column"
      style={{ height: "calc(100vh - 20px)", paddingTop: 12, paddingBottom: 12 }}
    >
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-1">
        <div>
          <h2 className="mb-0">Briefings</h2>
          <p className="mb-0 text-muted">
            Exec meeting prep, executive briefings, daily briefings, and client
            status, pulled live from Notion.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={fetchSections}
          disabled={isLoading}
        >
          {isLoading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      {/* Category tabs */}
      <ul className="nav nav-tabs mt-2 mb-3">
        {sections.map((section) => (
          <li className="nav-item" key={section.key}>
            <button
              type="button"
              className={`nav-link ${
                activeSectionKey === section.key ? "active" : ""
              }`}
              onClick={() => handleSelectSection(section.key)}
              title={section.description}
            >
              {section.label}
            </button>
          </li>
        ))}
      </ul>

      {error ? (
        <div className="alert alert-warning py-2">{error}</div>
      ) : null}

      {isLoading ? (
        <div className="text-muted py-4">Loading briefings...</div>
      ) : (
        <div className="nb-layout flex-grow-1">
          {/* Date list */}
          <div className="nb-sidebar">
            {activeSection?.description ? (
              <p className="nb-section-desc">{activeSection.description}</p>
            ) : null}
            <input
              className="form-control form-control-sm mb-2"
              placeholder="Search dates..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {activeSection?.error ? (
              <div className="alert alert-warning py-2 small mb-0">
                Could not load this section from Notion.
              </div>
            ) : visibleEntries.length === 0 ? (
              <div className="text-muted small py-2">
                {search.trim()
                  ? "No dates match your search."
                  : "No briefings yet."}
              </div>
            ) : (
              <div className="nb-date-list">
                {visibleEntries.map((entry) => (
                  <button
                    type="button"
                    key={entry.id}
                    className={`nb-date-item ${
                      entry.id === selectedEntryId ? "active" : ""
                    }`}
                    onClick={() => setSelectedEntryId(entry.id)}
                  >
                    <span className="nb-date-label">{entry.dateLabel}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Briefing preview */}
          <div className="nb-content">
            {selectedEntry ? (
              <>
                <div className="nb-content-header">
                  <span className="nb-content-title">
                    {selectedEntry.title}
                  </span>
                  <a
                    href={selectedEntry.url}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-sm btn-outline-primary"
                  >
                    Open in Notion
                  </a>
                </div>
                <div className="nb-preview-panel">
                  <BriefingPreview
                    detail={selectedDetail}
                    entry={selectedEntry}
                    error={detailError}
                    isLoading={detailLoadingId === selectedEntry.id}
                  />
                </div>
              </>
            ) : (
              <div className="nb-empty text-muted">
                Select a date to view its briefing.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotionBriefingsPage;
